/**
 * Tenant Admin → Policy Override editor.
 *
 * Each tenant can override the platform default for Terms or Privacy
 * (or both, or neither — empty field means "inherit"). Saves to
 * ``tenant.policies.terms_md`` / ``policies.privacy_md`` via
 * ``PATCH /api/admin/policies``.
 *
 * Layout: two stacked editors mirroring SuperPolicies, but with an
 * extra "Currently inheriting from platform" badge when the tenant
 * hasn't customised that section yet, plus a one-tap action that
 * pre-fills the textarea with the platform default (so the admin
 * has a starting point to tweak rather than a blank slate).
 */
import { useEffect, useState } from "react";
import {
    FileText, ShieldCheck, RefreshCw, CheckCircle2, Eye,
    Loader2, FileQuestion, Trash2,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminPolicies() {
    const [doc, setDoc] = useState(null);
    const [terms, setTerms] = useState("");
    const [privacy, setPrivacy] = useState("");
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get("/admin/policies");
            setDoc(data);
            setTerms(data.terms_md || "");
            setPrivacy(data.privacy_md || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        }
    };
    useEffect(() => { reload(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/policies", {
                terms_md: terms,
                privacy_md: privacy,
            });
            setDoc(data);
            toast.success("Saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setSaving(false); }
    };

    const loadPlatformTerms = () => {
        if (terms && !window.confirm("Replace your override with the platform default text? Your edits will be lost.")) return;
        setTerms(doc.platform_terms_md);
        toast.info("Platform default loaded — click Save to persist your copy");
    };
    const loadPlatformPrivacy = () => {
        if (privacy && !window.confirm("Replace your override with the platform default text? Your edits will be lost.")) return;
        setPrivacy(doc.platform_privacy_md);
        toast.info("Platform default loaded — click Save to persist your copy");
    };

    const clearTerms = () => {
        if (!window.confirm("Stop overriding Terms? Your customers will see the platform default again.")) return;
        setTerms("");
        save().catch(() => {});
    };
    const clearPrivacy = () => {
        if (!window.confirm("Stop overriding Privacy Policy? Your customers will see the platform default again.")) return;
        setPrivacy("");
        save().catch(() => {});
    };

    if (!doc) return (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>
    );

    return (
        <div data-testid="admin-policies-page">
            <div>
                <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                    <ShieldCheck size={22} /> Legal Policies
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Customise Terms &amp; Conditions and Privacy Policy for <b>your tenant only</b>.
                    Leave a section blank to inherit the platform default. Customers
                    see the effective policy at <code className="text-xs">/legal/terms</code> and
                    <code className="text-xs"> /legal/privacy</code>.
                </p>
            </div>

            <div className="mt-6 space-y-8">
                <OverrideEditor
                    testidPrefix="ap-terms"
                    title="Terms & Conditions"
                    icon={FileText}
                    md={terms}
                    setMd={setTerms}
                    inherits={doc.inherits_terms}
                    onLoadPlatform={loadPlatformTerms}
                    onClear={clearTerms}
                    onPreview={() => setPreview("terms")}
                />
                <OverrideEditor
                    testidPrefix="ap-privacy"
                    title="Privacy Policy"
                    icon={ShieldCheck}
                    md={privacy}
                    setMd={setPrivacy}
                    inherits={doc.inherits_privacy}
                    onLoadPlatform={loadPlatformPrivacy}
                    onClear={clearPrivacy}
                    onPreview={() => setPreview("privacy")}
                />
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 sticky bottom-4 bg-background/80 backdrop-blur rounded-2xl border border-border p-3">
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> Placeholders are substituted server-side
                    with your tenant&apos;s branding.
                </span>
                <button
                    data-testid="ap-save"
                    onClick={save}
                    disabled={saving}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm font-bold bg-primary text-primary-foreground rounded-xl px-4 py-2 disabled:opacity-60"
                >{saving && <Loader2 size={13} className="animate-spin" />}Save</button>
            </div>

            {preview && (
                <PreviewModal
                    md={
                        preview === "terms"
                            ? (terms || doc.platform_terms_md)
                            : (privacy || doc.platform_privacy_md)
                    }
                    title={preview === "terms" ? "Terms preview" : "Privacy preview"}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>
    );
}

function OverrideEditor({
    testidPrefix, title, icon: Icon, md, setMd, inherits,
    onLoadPlatform, onClear, onPreview,
}) {
    return (
        <section data-testid={`${testidPrefix}-editor`} className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h2 className="text-lg font-bold inline-flex items-center gap-2"><Icon size={18} /> {title}</h2>
                <div className="flex items-center gap-2">
                    {inherits && (
                        <span data-testid={`${testidPrefix}-inheriting`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 rounded-full px-2 py-0.5">
                            <FileQuestion size={11} /> Inheriting platform default
                        </span>
                    )}
                    <button
                        data-testid={`${testidPrefix}-preview`}
                        onClick={onPreview}
                        className="inline-flex items-center gap-1 text-xs font-bold bg-muted rounded-xl px-2.5 py-1.5"
                    ><Eye size={12} /> Preview</button>
                    <button
                        data-testid={`${testidPrefix}-load-platform`}
                        onClick={onLoadPlatform}
                        className="inline-flex items-center gap-1 text-xs font-bold bg-muted rounded-xl px-2.5 py-1.5"
                    ><RefreshCw size={12} /> Load platform default</button>
                    {!inherits && (
                        <button
                            data-testid={`${testidPrefix}-clear`}
                            onClick={onClear}
                            className="inline-flex items-center gap-1 text-xs font-bold bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400 rounded-xl px-2.5 py-1.5"
                        ><Trash2 size={12} /> Stop overriding</button>
                    )}
                </div>
            </div>
            <textarea
                data-testid={`${testidPrefix}-textarea`}
                value={md}
                onChange={(e) => setMd(e.target.value)}
                rows={16}
                spellCheck={false}
                className="w-full bg-muted/40 border border-border rounded-xl p-3 font-mono text-xs leading-relaxed outline-none focus:border-primary"
                placeholder={inherits
                    ? "Empty = inherit from platform. Paste your custom Markdown here to override."
                    : "Edit your tenant-specific Markdown here."}
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">{md.length.toLocaleString()} characters</p>
        </section>
    );
}

function PreviewModal({ md, title, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="ap-preview-modal">
            <div className="bg-background rounded-3xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="font-bold">{title}</h3>
                    <button data-testid="ap-preview-close" onClick={onClose} className="text-sm font-semibold px-2 py-1 hover:bg-muted rounded">Close</button>
                </header>
                <article className="overflow-y-auto px-5 py-4 text-sm
                    [&_h1]:text-xl [&_h1]:font-extrabold [&_h1]:mb-3
                    [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
                    [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mt-3
                    [&_p]:my-2 [&_p]:text-muted-foreground
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:text-muted-foreground
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                    [&_li]:my-0.5
                    [&_strong]:text-foreground [&_strong]:font-semibold
                    [&_em]:text-xs [&_em]:block [&_em]:mb-3
                    [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:italic">
                    <ReactMarkdown>{md}</ReactMarkdown>
                </article>
            </div>
        </div>
    );
}
